import React from 'react';
import { AppBar, Toolbar, Box, Button, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { SIGNAL_ORANGE } from '../../design/stoneTokens';
import PersonIcon from '@mui/icons-material/Person';
import TuneIcon from '@mui/icons-material/Tune';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

export const TopAppBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { userRole } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const isOperator = userRole === 'admin' || userRole === 'operator' || userRole === 'radar_operator';

  const navLinks = [
    { label: t('nav_home'), path: '/' },
    { label: t('nav_story'), path: '/story' },
    { label: t('nav_signal'), path: '/black-star-signal' },
    { label: t('nav_journal'), path: '/journal' },
    { label: t('nav_labs'), path: '/creative-lab' },
    { label: t('nav_archives'), path: '/archives' },
    { label: t('nav_news'), path: '/news' },
    {
      label: t('nav_communications'),
      path: '/communications/community',
      activePath: '/communications',
    },
  ];

  if (!isDesktop) return null;

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: 'rgba(17, 19, 24, 0.8)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
        boxShadow: 'none',
        zIndex: 1300,
      }}
    >
      <Toolbar sx={{ px: { md: 4, lg: 6 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            maxWidth: 1200,
            mx: 'auto',
          }}
        >
          {/* Logo container */}
          <Box
            onClick={() => navigate('/')}
            aria-label="Go to TogeeVision home"
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flex: '1 1 0px',
              justifyContent: 'flex-start',
            }}
          >
            <Box
              component="img"
              src="/images/tvisio_logo.png"
              alt="TogeeVision"
              width="128"
              height="128"
              sx={{
                display: 'block',
                width: 54,
                height: 54,
                objectFit: 'contain',
                filter: `drop-shadow(0 0 10px ${SIGNAL_ORANGE})`,
              }}
            />
          </Box>

          {/* Links container */}
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 auto',
            }}
          >
            {navLinks.map((link) => {
              const activePath = link.activePath || link.path;
              const isActive = activePath === '/'
                ? location.pathname === '/'
                : location.pathname === activePath || location.pathname.startsWith(`${activePath}/`);
              return (
                <Button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  sx={{
                    color: isActive ? SIGNAL_ORANGE : 'rgba(255, 255, 255, 0.6)',
                    fontWeight: isActive ? 700 : 500,
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    fontFamily: '"Manrope", sans-serif',
                    '&:hover': {
                      color: SIGNAL_ORANGE,
                      backgroundColor: 'transparent',
                    },
                    '&::after': isActive ? {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '25%',
                      right: '25%',
                      height: '2px',
                      backgroundColor: SIGNAL_ORANGE,
                      borderRadius: '2px',
                      boxShadow: `0 0 8px ${SIGNAL_ORANGE}`,
                    } : {},
                  }}
                >
                  {link.label}
                </Button>
              );
            })}

            {/* Control Panel icon — visible to admin, operator, and radar_operator */}
            {isOperator && (
              <IconButton
                onClick={() => navigate('/operator/ledger?tab=llm')}
                title="Control Panel"
                sx={{
                  color: location.pathname.startsWith('/operator') ? SIGNAL_ORANGE : 'rgba(255,255,255,0.4)',
                  '&:hover': { color: SIGNAL_ORANGE },
                }}
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Right actions container */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flex: '1 1 0px',
              justifyContent: 'flex-end',
            }}
          >
            {/* Language Toggle */}
            <Button
              onClick={toggleLanguage}
              sx={{
                color: SIGNAL_ORANGE,
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.1em',
                border: `1px solid rgba(255, 255, 255, 0.1)`,
                borderRadius: '4px',
                px: 1.5,
                py: 0.5,
                minWidth: 'auto',
                '&:hover': {
                  borderColor: SIGNAL_ORANGE,
                  backgroundColor: 'rgba(211, 118, 51, 0.1)'
                }
              }}
            >
              {language === 'en' ? 'FR' : 'EN'}
            </Button>

            <IconButton
              onClick={() => navigate('/profile')}
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                '&:hover': { color: SIGNAL_ORANGE },
              }}
            >
              <PersonIcon />
            </IconButton>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopAppBar;
